import { ActivatedRoute, Router } from '@angular/router';
import { Component, inject, OnInit, Signal, WritableSignal, computed, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';
import { take } from 'rxjs/operators';

import { AddressUtils } from '../../../utils/address.utils';
import { AnalyticsService } from '../../../analytics/analytics.service';
import { AppUtils } from '../../../utils/app.utils';
import { ConfigurationFacade } from '../../../facades/configuration.facade';
import { CustomerFacade } from '../../../facades/customer.facade';
import {
  EFeatureToggles,
  EStoreFeatures,
  EThankYouPageFieldNames,
  EThankYouPagePriceFieldNames,
} from '../../../configurations/common';
import { EPaymentExpensesName, EPaymentProviderName } from '../../../models/checkout.models';
import {
  ICalculatedDiscounts,
  IExpenses,
  IOrderById,
  IOrderCustomer,
  IOrderDataAttributes,
  IOrderItem,
  IPayments,
} from '../../../models/order.models';
import { ImagePreset } from '../../../models/image.models';
import { ImageUtils } from '../../../utils/image.utils';
import { OrdersFacade } from '../../../facades/orders.facade';
import { OrdersUtils } from '../../../utils/orders.utils';
import { PageTypes } from '../../../analytics/enums/pageTypes';
import { StringUtils } from '../../../utils/string.utils';
import { UiUtils } from '../../../utils/ui.utils';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-harmonized-page-order-thank-you',
  templateUrl: './harmonized-page-order-thank-you.component.html',
  styleUrl: './harmonized-page-order-thank-you.component.scss',
  standalone: false,
})
export class HarmonizedPageOrderThankYouComponent implements OnInit {
  isSapStore: boolean = AppUtils.isSapStore();

  protected readonly AddressUtils: typeof AddressUtils = AddressUtils;
  protected readonly EThankYouPageFieldNames: typeof EThankYouPageFieldNames = EThankYouPageFieldNames;
  protected readonly EThankYouPagePriceFieldNames: typeof EThankYouPagePriceFieldNames = EThankYouPagePriceFieldNames;
  protected readonly ImagePreset: typeof ImagePreset = ImagePreset;
  protected readonly ImageUtils: typeof ImageUtils = ImageUtils;
  protected readonly OrdersUtils: typeof OrdersUtils = OrdersUtils;
  protected readonly StringUtils: typeof StringUtils = StringUtils;
  protected readonly UiUtils: typeof UiUtils = UiUtils;
  protected readonly window: Window = window;

  private analyticsService: AnalyticsService = inject(AnalyticsService);
  private configurationFacade: ConfigurationFacade = inject(ConfigurationFacade);
  private customerFacade: CustomerFacade = inject(CustomerFacade);
  private ordersFacade: OrdersFacade = inject(OrdersFacade);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private router: Router = inject(Router);
  private translateService: TranslateService = inject(TranslateService);

  isSapP40Enabled: boolean = false;
  isDownloadReturnDocumentEnabled: boolean = false;
  isSubmitReturnDocumentEnabled: boolean = false;
  isDiscontinuedStatusEnabled: boolean = false;
  isExternalQuoteCheckoutEnabled: boolean = false;
  isProductVariantsEnabled: boolean = false;
  isProductTagsActive: boolean = this.configurationFacade.isFeatureAvailable(EStoreFeatures.PRODUCT_TAGS);

  pageFields: string[] = [];
  priceFields: string[] = [];

  companyUserId: string = '';
  guestUserId: string = '';
  orderReference: string = '';

  orderData: WritableSignal<IOrderDataAttributes | null> = signal<IOrderDataAttributes | null>(null);
  isOrderDataLoading: boolean = true;

  /**
   * Computed signal that returns the customer data from the order.
   */
  customer: Signal<IOrderCustomer | undefined> = computed<IOrderCustomer | undefined>(() =>
    this.orderData()?.customer,
  );

  /**
   * Computed signal that indicates whether the order belongs to the currently logged-in user.
   */
  isPersonalOrder: Signal<boolean> = computed<boolean>(() => {
    const companyUserUuid: string = this.customer()?.uuidCompanyUser ?? '';

    if (!companyUserUuid || this.isExternalQuoteCheckoutEnabled) {
      return true;
    }

    return this.companyUserId === companyUserUuid;
  });

  /**
   * Computed signal that returns the full name of the customer.
   */
  customerFullName: Signal<string> = computed<string>(() =>
    `${this.customer()?.firstName} ${this.customer()?.lastName}`,
  );

  /**
   * Signal containing the set of address fields used in template to render address blocks.
   */
  addressFields: Signal<Set<string>> = signal(new Set<string>([
    EThankYouPageFieldNames.SOLD_TO_ADDRESS,
    EThankYouPageFieldNames.SHIP_TO_ADDRESS,
    EThankYouPageFieldNames.BILL_TO_ADDRESS,
    EThankYouPageFieldNames.PAYER_ADDRESS,
  ]));

  /**
   * Computed signal that indicates whether the order is eligible for return.
   */
  isEligibleForReturn: Signal<boolean> = computed<boolean>(() => {
      if (!this.orderData()) {
        return false;
      }

      return OrdersUtils.isSapOrderEligibleForReturn(this.orderData() as IOrderDataAttributes);
    },
  );

  /**
   * Computed signal that builds the email link (recipient and subject) for the submit return document operation.
   */
  returnDocumentEmail: Signal<string> = computed<string>(() => {
    const emailRecipient: string = this.translateService.instant('config.return-document-recipient-email');
    const subject: string = encodeURIComponent(`Return Document ${this.orderData()?.sapDetails?.returnAuthorizationNumber}`);
    return `${emailRecipient}?Subject=${subject}`;
  });

  /**
   * Computed signal that returns calculated discounts as an array converted from the BE response.
   */
  discounts: Signal<ICalculatedDiscounts[]> = computed<ICalculatedDiscounts[]>(() =>
    Object.values(this.orderData()?.calculatedDiscounts ?? {}),
  );

  /**
   * Computed signal that returns whether TDS data should be displayed.
   */
  shouldTdsBeShown: Signal<boolean> = computed<boolean>(() => {
    const data: IOrderDataAttributes | null = this.orderData();
    const payments: IPayments[] = data?.payments as IPayments[] ?? [];
    const expenses: IExpenses[] = data?.expenses ?? [];
    return payments.some(payment => payment.paymentProvider === EPaymentProviderName.CC_AVENUE) &&
      expenses.some(expense => expense.name === EPaymentExpensesName.TDS);
  });

  /**
   * Computed signal that returns the TDS expense amount from the order expenses.
   */
  tdsExpense: Signal<number | undefined> = computed<number | undefined>(() =>
    this.orderData()?.expenses?.find(
      expense => expense.name === EPaymentExpensesName.TDS,
    )?.sumGrossPrice,
  );

  /**
   * Computed signal that returns the payment method translation key based on the payment provider.
   */
  paymentMethodTranslationKey: Signal<string> = computed<string>(() => {
    const payments: IPayments[] = this.orderData()?.payments as IPayments[] ?? [];
    return OrdersUtils.getPaymentTranslationKey(payments[0]?.paymentProvider ?? '');
  });

  ngOnInit(): void {
    this.initPageData();
  }

  /**
   * Initializes the necessary page data.
   * If "orderId" is not present in the route params, the user is redirected to the error page.
   * After initialization is successfully completed, it begins to get the order related data.
   */
  initPageData(): void {
    combineLatest([
      this.route.params,
      this.configurationFacade.getDynamicOrderThankYouPageFields(),
      this.configurationFacade.getDynamicOrderThankYouPagePriceFields(),
      this.configurationFacade.isFeatureEnabled(EFeatureToggles.SAP_P40),
      this.configurationFacade.isFeatureEnabled(EFeatureToggles.DOWNLOAD_RETURN_DOCUMENT),
      this.configurationFacade.isFeatureEnabled(EFeatureToggles.SUBMIT_RETURN_DOCUMENT),
      this.configurationFacade.isFeatureEnabled(EFeatureToggles.PRODUCT_DISCONTINUED_STATUS),
      this.configurationFacade.isFeatureEnabled(EFeatureToggles.EXTERNAL_QUOTE_CHECKOUT),
      this.configurationFacade.isFeatureEnabled(EFeatureToggles.PRODUCT_VARIANTS),
      this.customerFacade.selectUserId(),
    ]).pipe(
      take(1),
    ).subscribe(
      ([
         params,
         pageFields,
         priceFields,
         isSapP40Enabled,
         isDownloadReturnDocumentEnabled,
         isSubmitReturnDocumentEnabled,
         isDiscontinuedStatusEnabled,
         isExternalQuoteCheckoutEnabled,
         isProductVariantsEnabled,
         companyUserId,
       ]) => {
        this.isExternalQuoteCheckoutEnabled = isExternalQuoteCheckoutEnabled;
        this.orderReference = this.isExternalQuoteCheckoutEnabled ? params.guestOrderId : params.orderId;

        if (!this.orderReference) {
          this._redirectToErrorPage();
          return;
        }

        if (this.isExternalQuoteCheckoutEnabled) {
          this.guestUserId = params.guestUserId;
        } else {
          this._reloadOrders();
          this.companyUserId = companyUserId ?? '';
        }

        this.isSapP40Enabled = isSapP40Enabled;
        this.isDownloadReturnDocumentEnabled = isDownloadReturnDocumentEnabled;
        this.isSubmitReturnDocumentEnabled = isSubmitReturnDocumentEnabled;
        this.isDiscontinuedStatusEnabled = isDiscontinuedStatusEnabled;
        this.isProductVariantsEnabled = isProductVariantsEnabled;
        this.pageFields = pageFields;
        this.priceFields = priceFields;

        this.getOrderData();
      },
    );
  }

  /**
   * Fetches the order related data by order ID.
   * If any error occurs, the user is redirected to the error page.
   */
  getOrderData(): void {
    this.ordersFacade.getOrderById(this.orderReference, this.guestUserId).pipe(
      take(1),
    ).subscribe({
      next: (orderResponseWithMessages: IOrderById) => {
        this.orderData.set(orderResponseWithMessages.data?.attributes as IOrderDataAttributes);
        this.isOrderDataLoading = false;
        this._triggerPageLoadedAnalyticsEvent();
        if (!this.isExternalQuoteCheckoutEnabled && this.isSapP40Enabled) {
          this.ordersFacade.recallAndClearOrdersWithDetailsDataList();
        }
      },
      error: () => {
        this.isOrderDataLoading = false;
        this._redirectToErrorPage();
      },
    });
  }

  /**
   * Triggers the page loaded analytics event.
   *
   * @private
   */
  private _triggerPageLoadedAnalyticsEvent(): void {
    this.analyticsService.setProducts(this.orderData()?.items);
    this.analyticsService.trackPageReady('Order Thank You', PageTypes.ORDER_THANK_YOU_PAGE, 'thank-you-page');
  }

  /**
   * Redirects the user to the error page.
   *
   * @private
   */
  private _redirectToErrorPage(): void {
    this.router.navigate(['page-not-found']).then();
  }

  /**
   * Returns the unit price for given item.
   * If isProductVariantsEnabled toggle is enabled and the item contains unitPriceExcludingHousingFee, return unitPriceExcludingHousingFee.
   * Otherwise, returns unitPrice.
   */
  getItemUnitPrice(item: IOrderItem): number {
    if (this.isProductVariantsEnabled && item.unitPriceExcludingHousingFee != null) {
      return item.unitPriceExcludingHousingFee;
    }
    return item.unitPrice ?? 0;
  }

  /**
   * Downloads the return document by opening the return document URL in a new tab.
   */
  downloadReturnDocument(): void {
    this.window.open(this.translateService.instant(environment.sparePartsReturnDocumentUrlKey), '_blank');
  }

  /**
   * Method for reloading orders in state after new order is created.
   *
   * @private
   */
  private _reloadOrders(): void {
    if (AppUtils.isSapStore()) {
      this.ordersFacade.recallAndClearOrdersWithDetailsDataList();
    } else {
      this.ordersFacade.loadOrdersHistory(undefined, undefined, true);
    }
  }
}
